/**
 * The deal lifecycle, in one place.
 *
 * Before this file, five services each carried their own hand-written copy of
 * "which statuses mean sold" and "which mean we still own the car":
 * xeroService, reportsService, commissionService, dashboardService and
 * commissionReconService. They had already drifted - none of them knew about
 * sold_in_transit or sold_delivered, so a car on either read as *not sold* to
 * the GST register, the P&L, the commission report and the dashboard alike.
 *
 * That went unnoticed only because every imported vehicle sits on the plain
 * 'sold' status. Winnie's 10 Sep change retires that stage, so from now on every
 * sale lands on exactly the two statuses those lists were missing.
 *
 * One list, imported everywhere. Adding a stage means editing this file, and the
 * whole platform agrees about it on the same deploy.
 *
 * Typed as readonly string[] rather than a const tuple on purpose: every caller
 * tests a deal_status that is plainly a string, and a literal-union tuple would
 * make .includes() a type error at each of those call sites.
 */

/** Committed to buy, not yet paid. Not stock, not a sale. */
export const PIPELINE_STATUSES: readonly string[] = [
  'sourced',
  'under_offer',
  'contract_signed',
  'committed',
  'approved_for_payment',
]

/**
 * We own the car and it has not left. 'listed' is retired but kept here, because
 * historical rows still sit on it and they are still real stock.
 */
export const ON_HAND_STATUSES: readonly string[] = ['paid', 'listed', 'deal_pending']

/**
 * The car has left. 'sold' and 'awaiting_payment' are retired stages kept for the
 * imported history; sold_in_transit and sold_delivered are where sales land now.
 *
 * 'closed' belongs here because a deal is only closed after it sold - a deal that
 * never sold is cancelled, not closed.
 */
export const SOLD_STATUSES: readonly string[] = [
  'sold',
  'sold_in_transit',
  'sold_delivered',
  'awaiting_payment',
  'closed',
]

/** Money has gone out, so the vehicle is on the books: on hand, or sold. */
export const ACQUIRED_STATUSES: readonly string[] = [...ON_HAND_STATUSES, ...SOLD_STATUSES]

/** A binding contract exists. Everything from Contract Signed onward. */
export const SIGNED_STATUSES: readonly string[] = [
  'contract_signed',
  'committed',
  'approved_for_payment',
  ...ACQUIRED_STATUSES,
]

const onHandSet = new Set<string>(ON_HAND_STATUSES)
const soldSet = new Set<string>(SOLD_STATUSES)
const acquiredSet = new Set<string>(ACQUIRED_STATUSES)
const signedSet = new Set<string>(SIGNED_STATUSES)

export const isOnHandStatus = (s: string | null | undefined): boolean => onHandSet.has(String(s || ''))
export const isSoldStatus = (s: string | null | undefined): boolean => soldSet.has(String(s || ''))
export const isAcquiredStatus = (s: string | null | undefined): boolean => acquiredSet.has(String(s || ''))
export const isSignedStatus = (s: string | null | undefined): boolean => signedSet.has(String(s || ''))
